import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { NoteTemplate } from './note-template.component';
import { SharedModule } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CheckboxModule } from 'primeng/checkbox';
import { InputMaskModule } from 'primeng/inputmask';
import { TooltipModule } from 'primeng/tooltip';
import { IExternalUser } from '@core/model';
import { AuthenticationService } from '@core/authentication/services';
import { PanelModule } from 'primeng/panel';
import { NotesService } from '@feature/user-details/account-notes/notes.service';
import { NoteTemplateContainerComponent } from '@feature/user-details/account-notes/account-note/note-template-container.component';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const Number_Of_Attempts = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 }
];

const SuccessfulBool = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
];

@Component({
  selector: 'app-account-note',
  templateUrl: './account-note.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PanelModule,
    SelectModule,
    TextareaModule,
    DatePickerModule,
    RadioButtonModule,
    SharedModule,
    InputTextModule,
    ButtonModule,
    SelectButtonModule,
    ReactiveFormsModule,
    CheckboxModule,
    InputMaskModule,
    TooltipModule,
    NoteTemplateContainerComponent,
  ]
})
export class AccountNoteComponent extends NoteTemplate<any> implements OnInit, OnChanges {
  charCount: number = 0;
  characterCount: number = 0;
  today: Date = new Date();

  @Input() _isNoteEditable = false;
  @Input() isExisting: boolean = false;
  @Input() noteDetails: any;
  @Input() showNote: boolean = false;
  @Input() userData!: IExternalUser;

  @Output() cancelClicked = new EventEmitter<void>();
  @Output() noteSaved = new EventEmitter<{ note: any; result: any; type: string }>();

  override isExistingNote!: boolean;
  YesNoOptions = SuccessfulBool;
  outreachAttempt = Number_Of_Attempts;
  validationItems = [
    {
      label: 'Contact information for the office staff who are assisting with validation.',
      value: 'contactInfo'
    },
    {
      label: "This is the user's business email.",
      value: 'businessEmail'
    },
    {
      label: 'Obtain all NPI numbers associated with the account, Tax IDs, and all locations.',
      value: 'npiTaxLocation'
    }
  ];

  constructor(
    protected override readonly notesService: NotesService,
    protected readonly authenticationService: AuthenticationService,
    private readonly formBuilder: FormBuilder
  ) {
    super(notesService);
  }

  override createForm(): FormGroup {
    return this.formBuilder.group({
      InternalPortalNoteId: new FormControl(EMPTY_GUID),
      noteType: new FormControl('Account Note'),
      noteDate: new FormControl(new Date()),
      createdBy: new FormControl(),
      createdOn: new FormControl(),
      modifiedBy: new FormControl(),
      modifiedOn: new FormControl(),
      note: new FormControl(null, [
        Validators.required,
        Validators.maxLength(500),
        noWhiteSpaceValidator
      ]),
      name: new FormControl(null, [
        Validators.required,
        Validators.maxLength(50),
        noWhiteSpaceValidator
      ]),
      phone: new FormControl(null, [
        Validators.required,
        Validators.maxLength(10),
        Validators.pattern('[0-9]{10}')
      ]),
      ext: new FormControl(null),
      email: new FormControl(null, [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
      ]),
      requestDate: new FormControl(null, [Validators.required]),
      activityType: new FormControl(null, [Validators.required]),
      requestSource: new FormControl(null, [Validators.required]),
      otherActivityType: new FormControl(null),
      otherRequestSource: new FormControl(null),
      userId: new FormControl(),
      noteId: new FormControl(EMPTY_GUID),
      tenantId: new FormControl(EMPTY_GUID),
      noteCategoryName: new FormControl('Account Note'),
      entityType: new FormControl(),
      infoValidated: new FormControl(null),
      outreachAttempt: new FormControl(null),
      validatedItems: this.formBuilder.array(
        this.validationItems.map(() => this.formBuilder.control(false))
      ),
      outreachAttemptDate: new FormControl(null),
      administratorName: new FormControl(null),
      verifyAccount: new FormControl(null),
      verifiedDate: new FormControl(this.today)
    });
  }

  ngOnInit(): void {
    this.isExistingNote = this.isExisting;
    if (this.isExistingNote !== undefined) {
      this.init();
    }
    if (this._isNoteEditable && this.noteForm) {
      this.noteForm.enable();
    }
    this.noteForm.get('note')?.valueChanges.subscribe(value => {
      this.charCount = value?.length || 0;
    });
    this.noteForm.get('administratorName')?.valueChanges.subscribe(value => {
      this.characterCount = value?.length || 0;
    });

    this.noteForm.get('activityType')?.valueChanges.subscribe(value => {
      const otherControl = this.noteForm.get('otherActivityType');

      if (value === 'Other') {
        otherControl?.setValidators([
          Validators.required,
          Validators.maxLength(30),
          noWhiteSpaceValidator
        ]);
      } else {
        otherControl?.clearValidators();
        otherControl?.setValue(null);
      }

      otherControl?.updateValueAndValidity();

      if (value === 'Common Domain Email Verification') {
        this.setCommonDomainValidators();
      } else {
        this.clearCommonDomainValidators();
      }
    });
    this.noteForm.get('requestSource')?.valueChanges.subscribe(value => {
      const otherSourceControl = this.noteForm.get('otherRequestSource');

      if (value === 'Other') {
        otherSourceControl?.setValidators([
          Validators.required,
          Validators.maxLength(30),
          noWhiteSpaceValidator
        ]);
      } else {
        otherSourceControl?.clearValidators();
        otherSourceControl?.setValue(null);
      }

      otherSourceControl?.updateValueAndValidity();
    });

    if (this.noteForm && this.userData && !this.isExisting) {
      this.patchUserData();
    } else if (this.isExisting && this.noteForm) {
      this.patchFormWithExistingNote();
    }

    this.noteForm.get('verifyAccount')?.valueChanges.subscribe(value => {
      const verifiedDateCtrl = this.noteForm.get('verifiedDate');
      if (value === true || value === 'Yes') {
        verifiedDateCtrl?.setValidators([Validators.required]);
        if (!verifiedDateCtrl?.value) {
          verifiedDateCtrl?.setValue(new Date());
        }
      } else {
        verifiedDateCtrl?.clearValidators();
        verifiedDateCtrl?.setValue(null);
      }
      verifiedDateCtrl?.updateValueAndValidity();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isExisting']) {
      this.isExistingNote = changes['isExisting'].currentValue;
    }

    if (changes['userData'] && changes['userData'].currentValue && this.noteForm) {
      this.patchUserData();
    }

    if (changes['noteDetails']?.currentValue && this.isExisting && this.noteForm) {
      Promise.resolve().then(() => {
        this.patchFormWithExistingNote();
      });
    }

    if (changes['_isNoteEditable']) {
      if (this._isNoteEditable && this.noteForm && this.isExistingNote) {
        this.noteForm.enable();
      }
    }
  }

  patchUserData(): void {
    if (this.userData) {
      this.noteForm.patchValue({
        name: this.userData.displayName || '',
        phone: this.userData.phone || '',
        email: this.userData.email || '',
        healthPlan: this.userData.healthPlans?.[0] || '',
        ext: this.userData.phoneExtension || '',
        userId: this.userData.userId || ''
      });
      const controls = ['ext', 'name', 'email'];
      controls.forEach(controlName => {
        const control = this.noteForm.get(controlName);
        if (control) {
          control.markAsTouched();
          control.markAsDirty();
          control.updateValueAndValidity();
        }
      });
    }
  }

  onSave() {
    const model = this.noteForm.value;

    model.validatedItems = this.noteForm.value.validatedItems
      .map((checked: boolean, i: number) => (checked ? this.validationItems[i].value : null))
      .filter((v: string | null) => v !== null);

    if (this.isExisting) {
      model.modifiedBy = this.authenticationService.userEmail;
    }
    this.save(model);
  }

  protected override onSaved(note: any, result: any, type: string): void {
    this.noteSaved.emit({ note, result, type });
    this.onCancel();
  }

  onCancel() {
    this.noteForm.reset();
    this.patchUserData();
    this.cancelClicked.emit();
  }

  resetForm() {
    this.noteForm.reset();
    this.patchUserData();
  }

  override patchFormWithExistingNote(): void {
    const validatedValues = this.noteDetails?.validatedItems || [];
    const validatedFormArray = this.noteForm.get('validatedItems') as FormArray;
    validatedFormArray.controls.forEach((control, index) => {
      const itemValue = this.validationItems[index].value;
      control.setValue(validatedValues.includes(itemValue));
    });

    this.noteForm.patchValue({
      ...this.noteDetails,
      requestDate: this.noteDetails?.requestDate ? new Date(this.noteDetails.requestDate) : null,
      verifiedDate: this.noteDetails?.verifiedDate ? new Date(this.noteDetails.verifiedDate) : null,
      outreachAttemptDate: this.noteDetails?.outreachAttemptDate
        ? new Date(this.noteDetails.outreachAttemptDate)
        : null
    });
  }

  validatePastDateTime(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const selectedDate = new Date(value);
    const now = new Date();

    return selectedDate > now ? { futureTimeNotAllowed: true } : null;
  }

  private setCommonDomainValidators() {
    this.noteForm.get('infoValidated')?.setValidators([Validators.required]);
    this.noteForm.get('outreachAttempt')?.setValidators([Validators.required]);
    this.noteForm.get('outreachAttemptDate')?.setValidators([Validators.required]);
    this.noteForm.get('verifyAccount')?.setValidators([Validators.required]);
    this.noteForm.get('verifiedDate')?.setValidators([Validators.required]);
    this.noteForm
      .get('administratorName')
      ?.setValidators([Validators.maxLength(500), noWhiteSpaceValidator, Validators.required]);
    this.noteForm.updateValueAndValidity();
  }
  private clearCommonDomainValidators() {
    [
      'infoValidated',
      'outreachAttempt',
      'validatedItems',
      'outreachAttemptDate',
      'verifyAccount',
      'verifiedDate',
      'administratorName'
    ].forEach(ctrl => {
      this.noteForm.get(ctrl)?.clearValidators();
      this.noteForm.get(ctrl)?.updateValueAndValidity();
    });
  }

  get hasValidatedItems(): boolean {
    const validated = this.noteForm.value.validatedItems;
    return (
      Array.isArray(validated) && this.validationItems.some(item => validated.includes(item.value))
    );
  }
}
